/** 조회 조건 — `POST /api/v1/get-all-return-inbounds` 요청 본문. */
export interface ReturnInboundSearchParams {
  /** 화주명 다건 (BE 최대 200) */
  shipperNames?: string[]
  /** 화주유형 다건 (1.0 service_type) */
  logisticsServiceTypes?: string[]
  /** 주문번호 다건 (BE 최대 200) */
  ordNumbers?: string[]
  /** 입고번호 다건 (BE 최대 200) */
  ordOptionNumbers?: string[]
  orderStatus?: string
  /** 입고예정일자 시작 (YYYY-MM-DD) */
  plnDateFrom?: string
  /** 입고예정일자 종료 (YYYY-MM-DD) */
  plnDateTo?: string
  /** 입고실적일자 기간 (YYYY-MM-DD) — BE 가 양끝 포함 BETWEEN 으로 거르고 한쪽만 주면 400 이라 둘을 함께 보낸다(LOGISPRD-14905). */
  actDateFrom?: string
  actDateTo?: string
  page: number
  size: number
}

/** 비밀번호 zip 엑셀(`/download`) 요청 — 조회 필터(페이징 제외, BE `AllReturnInboundDownloadRequest`) + RGR02·RGR09 와 같은 사유·비밀번호 계약. */
export type ReturnInboundDownloadParams = Omit<ReturnInboundSearchParams, 'page' | 'size'> & {
  reasonCode: string
  detailedReason: string
  password: string
}

/**
 * 한 구간에 받아 오는 건수 = BE `size` 상한(`@field:Max(1000)`). 전건은 이 크기로 page 를 이어 받는다(LOGISPRD-15269).
 *
 * 핸드오프 데이터 계약이 `fetchTable(): Promise<TableRow[]>` — 페이징 인자가 없는 전건 조회다.
 * 표의 페이징·정렬·필터·목록검색이 모두 받아 둔 전건을 대상으로 돌아야 1.0(페이지당 10,000행
 * 무한스크롤 + ag-grid 클라이언트 정렬) 과 같은 답을 낸다.
 */
export const MAX_FETCH_SIZE = 1000

/**
 * 날짜는 `yyyyMMdd` 로 보낸다 — BE 가 `LocalDate.parse(value, FORMATTER_YYYYMMDD)` 로 읽어
 * ISO(`2026-08-27`)를 주면 "날짜 형식이 올바르지 않습니다 (yyyyMMdd)" 로 400 이 난다.
 * 화면·클라이언트 필터는 ISO 로 통일돼 있어 전송 직전 이 함수에서만 바꾼다(조회·엑셀 공용).
 */
const toApiDate = (value?: string) => value?.replace(/-/g, '')

export const toSearchPayload = (params: ReturnInboundSearchParams): ReturnInboundSearchParams => ({
  ...params,
  plnDateFrom: toApiDate(params.plnDateFrom),
  plnDateTo: toApiDate(params.plnDateTo),
  actDateFrom: toApiDate(params.actDateFrom),
  actDateTo: toApiDate(params.actDateTo),
})

/**
 * 조회 응답 행. BE `GetAllReturnInboundsResponse` 중 화면이 쓰는 필드만 선언한다.
 *
 * 그리드 행 타입으로 그대로 쓴다 — 표시 전용 화면이라 변환 계층을 두지 않는다.
 * (BE 필드명 = 컬럼 `field`, 값 가공은 컬럼 정의의 valueFormatter 가 맡는다)
 */
export interface ReturnInboundItem {
  id: number
  serviceType: string | null
  shipperName: string | null
  shipperCode: string
  orderType: string | null
  brandName: string | null
  goodsGroup: string | null
  goodsCode: string | null
  goodsName: string | null
  styleNumber: string | null
  goodsOption: string | null
  ordQty: number | null
  barcode: string | null
  skuId: string | null
  ordNumber: string | null
  ordOptionNumber: string | null
  orderStatus: string | null
  plnDate: string | null
  actDate: string | null
  actQty: number | null
  trsType: string | null
  trsTypeDetail: string | null
  trsLspNm: string | null
  drvName: string | null
  trsNumber: string | null
  trsDeptDt: string | null
  trsArrvDt: string | null
  refNo: string | null
  refOptNo: string | null
  procNm: string | null
  ordProcNm: string | null
  createdAt: string | null
  createdBy: string | null
  updatedAt: string | null
  updatedBy: string | null
  lotAttr5: string | null
  trsInvoiceNo: string | null
  refNo2: string | null
}

/** 주문상태 태그 색 — 키는 BE `InboundReportStatus.code` 실값(입고조회(전체) GR08 과 같은 색). */
export const ORDER_STATUS_TAG_COLORS: Record<string, string> = {
  입고대기: 'processing',
  부분입고: 'processing',
  입고완료: 'success',
  과입고: 'warning',
  입고취소: 'error',
}

/**
 * 입고상태 필터 옵션 — moms-be `InboundReportStatus.code` 5종이 정본이다(LOGISPRD-14464).
 * 핸드오프 spec 의 출고 값(출고완료/미출고/가송장)은 BE 가 거절해 조회가 실패한다.
 */
export const ORDER_STATUS_OPTIONS = ['입고대기', '부분입고', '입고완료', '과입고', '입고취소'] as const

/**
 * 입고유형 필터 옵션 — 핸드오프 spec §필터 (ORD_TYPE).
 *
 * BE 는 `ordType = RETURN` 고정이라 이 조건을 받지 않는다(moms-be#2410).
 * 응답 `orderType` 의 하위 구분(반품·교환 등)을 받은 행(상한 1,000) 안에서 화면이 거른다.
 */
export const ORDER_TYPE_OPTIONS = [
  '해외',
  '폐기',
  '테스트1',
  '증정',
  '재고이동',
  '일반',
  '사내(해외)',
  '사내',
  '반품',
  '명품',
  '교환',
  'PLUS',
] as const
